import { Link } from 'react-router-dom'
import { BookOpen, Clock, Lock, Sparkles, Wand2 } from 'lucide-react'
import { CefrBadge } from '@/components/CefrBadge'
import { LoadingSpinner } from '@/components/LoadingSpinner'
import { RecommendationWhyPanel } from '@/components/reading/RecommendationWhyPanel'
import { GeneratePersonalizedArticlePanel } from '@/components/reading/GeneratePersonalizedArticlePanel'
import type { RecommendedArticle } from '@/api/reading'
import type { CefrLevel } from '@/types/common'
import { formatMessage } from '@/i18n/format'
import { useMinCopy } from '@/i18n/useUiLocale'
import { maxArticleUnits } from '@/config/practiceLimits'
import { cn } from '@/components/ui/cn'

interface ReadingHubSectionProps {
  recommendation: RecommendedArticle | undefined
  isLoading: boolean
  isError: boolean
  onBrowseLibrary?: () => void
}

function RecommendedPanel({
  recommendation,
  isLoading,
  isError,
  onBrowseLibrary,
}: ReadingHubSectionProps) {
  const { reading, actions } = useMinCopy()

  if (isLoading) {
    return (
      <div
        className="flex min-h-[220px] items-center justify-center rounded-2xl border border-border/60 bg-card/50"
        data-testid="reading-hero-loading"
      >
        <LoadingSpinner />
      </div>
    )
  }

  if (isError || !recommendation) {
    return (
      <div className="flex min-h-[220px] flex-col justify-center rounded-2xl border border-dashed border-border/80 bg-muted/10 p-6">
        <p className="text-sm font-medium text-foreground">{reading.noRecommendationTitle}</p>
        <p className="mt-1 text-sm text-muted-foreground">{reading.noRecommendationHint}</p>
        <button
          type="button"
          onClick={onBrowseLibrary}
          className="mt-4 inline-flex h-9 w-fit items-center gap-2 rounded-lg bg-primary px-4 text-sm font-medium text-primary-foreground hover:bg-primary/90"
        >
          <BookOpen className="h-4 w-4" />
          {actions.browseReading}
        </button>
      </div>
    )
  }

  const { article, reasons, estimatedMinutes, explanations = [] } = recommendation

  return (
    <div
      className="group relative flex h-full flex-col overflow-hidden rounded-2xl border border-border/60 bg-card p-6 shadow-sm transition-shadow hover:shadow-md"
      data-testid="reading-recommendation-hero"
    >
      <div className="pointer-events-none absolute -right-8 -top-8 h-32 w-32 rounded-full bg-primary/5 blur-2xl" />

      <div className="mb-4 flex items-center gap-2">
        <span className="flex h-7 w-7 items-center justify-center rounded-lg bg-primary/10 text-primary">
          <Sparkles className="h-3.5 w-3.5" />
        </span>
        <span className="text-xs font-semibold uppercase tracking-widest text-primary">
          {reading.pickedForYou}
        </span>
      </div>

      <div className="mb-3 flex flex-wrap items-center gap-2">
        <CefrBadge level={article.cefrLevel as CefrLevel} />
        <span className="flex items-center gap-1 text-xs text-muted-foreground">
          <Clock className="h-3.5 w-3.5" />
          {formatMessage(reading.durationMinutesApprox, { minutes: estimatedMinutes })}
        </span>
      </div>

      <h2 className="mb-3 text-xl font-bold leading-snug tracking-tight text-foreground">
        {article.title}
      </h2>

      {reasons.length > 0 ? (
        <div className="mb-4 flex flex-wrap gap-1.5">
          {reasons.map((reason) => (
            <span
              key={reason}
              className="rounded-full bg-muted/80 px-2.5 py-0.5 text-xs text-muted-foreground"
            >
              {reason}
            </span>
          ))}
        </div>
      ) : null}

      <RecommendationWhyPanel explanations={explanations} className="mb-5" />

      <div className="mt-auto">
        <Link
          to={`/reading/${article.id}`}
          className="inline-flex h-10 w-full items-center justify-center gap-2 rounded-lg bg-primary text-sm font-medium text-primary-foreground shadow-sm transition-colors hover:bg-primary/90 sm:w-auto sm:px-6"
          data-testid="reading-hero-start"
        >
          <BookOpen className="h-4 w-4" />
          {actions.startReading}
        </Link>
      </div>
    </div>
  )
}

export function ReadingHubSection({
  recommendation,
  isLoading,
  isError,
  onBrowseLibrary,
}: ReadingHubSectionProps) {
  const { reading } = useMinCopy()

  return (
    <section data-testid="reading-hub">
      <div className="grid gap-4 lg:grid-cols-5 lg:gap-5">
        <div className="lg:col-span-2">
          <RecommendedPanel
            recommendation={recommendation}
            isLoading={isLoading}
            isError={isError}
            onBrowseLibrary={onBrowseLibrary}
          />
        </div>

        <div className="lg:col-span-3">
          <div
            className={cn(
              'relative flex h-full flex-col overflow-hidden rounded-2xl border border-violet-500/20',
              'bg-gradient-to-br from-violet-500/5 via-card to-card p-6 shadow-sm'
            )}
          >
            <div className="pointer-events-none absolute -bottom-6 -left-6 h-28 w-28 rounded-full bg-violet-500/5 blur-2xl" />

            <div className="mb-4 flex items-center gap-2">
              <span className="flex h-7 w-7 items-center justify-center rounded-lg bg-violet-500/10 text-violet-500 dark:text-violet-400">
                <Wand2 className="h-3.5 w-3.5" />
              </span>
              <span className="text-xs font-semibold uppercase tracking-widest text-violet-600 dark:text-violet-400">
                {reading.generatePersonalizedTitle}
              </span>
            </div>

            <p className="mb-4 text-sm leading-relaxed text-muted-foreground">
              {formatMessage(reading.generatePersonalizedHint, { max: maxArticleUnits })}
            </p>

            <div className="mb-4 flex items-start gap-2 rounded-lg border border-border/50 bg-muted/20 px-3 py-2.5">
              <Lock className="mt-0.5 h-3.5 w-3.5 shrink-0 text-muted-foreground" />
              <p className="text-xs leading-relaxed text-muted-foreground">
                {reading.generatePersonalizedPrivate}
              </p>
            </div>

            <div className="mt-auto">
              <GeneratePersonalizedArticlePanel embedded />
            </div>
          </div>
        </div>
      </div>
    </section>
  )
}
